import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    BasicBadge,
    BasicInput,
    Container,
    palette,
    Paper,
    PrimaryButton,
    SimpleToggle,
    TabsPanel,
    Typography as T,
} from '@dev/ee-components';
import { generateIdBasedOnPath, safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { replace } from 'redux-first-history';

import {
    getListstStore,
    ListAddStart,
    ListDeselect,
    ListEditStart,
    ListsLoadStart,
    SingleListLoadStart,
} from '@/store/lists/actions';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { links } from '@/services/dictionaries/links';

import { useSecurity } from '@/services/hooks/useSecurity';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';

const ScreenContainer = styled(Container)`
    & .board {
        padding: 0;
        margin-top: -1px;
    }
    & .optionWrapper {
        padding-top: 32px;
        padding-bottom: 12px;
    }
    & .toggleDescription {
        padding-left: 40px;
    }
    & .MuiFormControlLabel-label {
        font-family: Nunito;
        font-weight: 600;
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0px;
        color: #32325c;
        opacity: 1;
    }
    input {
        padding: 0;
        border: 0; //prevent 2018 styles
    }
    & .MuiAlert-root {
        margin: 24px 0;
        color: ${palette.gray[7]};
    }
    & .MuiRadio-root {
        padding: 2px 9px;
    }
`;

const ListsFC: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch: any = useDispatch();
    const { listname } = useParams<{ listname: string }>();
    const queryListName = listname && safelyDecodeBase64Url(listname);
    const [listName, setListName] = useState(queryListName);
    const [allowUnsubscribe, setAllowUnsubscribe] = useState(false);
    const [trackHistory, setTrackHistory] = useState(false);
    const [trackedListNo, setTrackedListNo] = useState(0);
    const { isReseller } = useSecurity();
    const { selected, collection, totalTrackedLists = 0 } = useSelector(getListstStore);

    useEffect(() => {
        if (!collection) {
            dispatch(ListsLoadStart({ limit: DEFAULT_PER_PAGE_COUNT, offset: 0 }));
        }

        setTrackedListNo(totalTrackedLists);
    }, [collection]);

    useEffect(() => {
        if (queryListName && !selected) {
            dispatch(SingleListLoadStart(queryListName));
        }
        if (selected) {
            setAllowUnsubscribe(selected.AllowUnsubscribe);
            setTrackHistory(selected.TrackHistory);
        }
    }, [selected]);

    useEffect(() => {
        dispatch(ListDeselect());
    }, []);

    const toggleSwitch = (e: ChangeEvent<HTMLInputElement>) => {
        const toggleName = e.target?.value;
        const switchToggle = {
            trackHistory: () => setTrackHistory((prev) => !prev),
            allowUnsubscribe: () => setAllowUnsubscribe((prev) => !prev),
        };
        return switchToggle?.[toggleName]();
    };
    const updateTrackHistoryNo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedValue = e.target?.checked ? 1 : -1;
        setTrackedListNo((prev) => prev + updatedValue);
    };
    const toggleTrackHistory = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateTrackHistoryNo(e);
        toggleSwitch(e);
    };
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setListName(e?.target?.value?.trim());
    };
    const handleSave = (): void => {
        if (queryListName) {
            const listData = {
                NewListName: listName !== queryListName ? listName : null,
                AllowUnsubscribe: allowUnsubscribe,
                TrackHistory: trackHistory,
            };
            dispatch(ListEditStart(queryListName, listData));
            return null;
        }
        dispatch(
            ListAddStart({
                CreateEmptyList: true,
                AllowUnsubscribe: allowUnsubscribe,
                TrackHistory: trackHistory,
                ListName: listName,
            })
        );
    };
    const goBackToList = () => {
        dispatch(
            replace(
                queryListName
                    ? ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(queryListName))
                    : ContactsPaths.Lists
            )
        );
    };
    return (
        <ScreenContainer>
            <TabsPanel
                title={queryListName ? t('editlist') : t('createlist')}
                additionalHeaderContent={
                    <PrimaryButton
                        id={generateIdBasedOnPath('save-list-button')}
                        text={t('savelist')}
                        disabled={!listName}
                        onClick={handleSave}
                    />
                }
                breadcrumbs={queryListName ? queryListName : t('lists')}
                breadcrumbsOnClick={goBackToList}
                tabBackground={'#fff'}
            />
            <Paper padding={{ top: 24, right: 48, bottom: 24, left: 48 }}>
                <div>
                    <BasicInput
                        id={generateIdBasedOnPath('new-list-name-input')}
                        label={t('listname')}
                        type="input"
                        defaultValue={queryListName}
                        placeholder={t('newlist')}
                        error={!isNil(listName) && !listName}
                        errorMessage={!isNil(listName) && !listName ? t('emptylistname') : t('duplicatedlistname')}
                        onChange={handleNameChange}
                    />
                    <div className="optionWrapper">
                        <SimpleToggle
                            id={generateIdBasedOnPath('new-list-name-input')}
                            value="allowUnsubscribe"
                            label={t('managesubscriptions')}
                            onChange={toggleSwitch}
                            checked={allowUnsubscribe}
                        />
                        <div className="toggleDescription">
                            <T variant="body1">
                                {t('managesubscriptionsdesc_old')}
                                {!isReseller && (
                                    <a href={links.unsubscribeSettings} rel="noreferrer" target="_blank">
                                        {t('learnmoreabout')} {t('managesubscriptionslink')}
                                    </a>
                                )}
                            </T>
                        </div>
                    </div>
                    <div className="optionWrapper">
                        <SimpleToggle
                            id={generateIdBasedOnPath('track-history-toggle')}
                            value="trackHistory"
                            disabled={!trackHistory && trackedListNo >= 10}
                            label={t('trackhistory')}
                            onChange={toggleTrackHistory}
                            checked={trackHistory || false}
                        />
                        <BasicBadge
                            style={{ position: 'relative', marginTop: '-5px', marginLeft: '-10px' }}
                            textColor={palette.indigo[6]}
                            backgroundColor={palette.gray[1]}
                            value={`${trackedListNo || 0}/10`}
                        />
                        <div className="toggleDescription">
                            <T variant="body1">{t('trackhistorydesc')}</T>
                        </div>
                    </div>
                </div>
            </Paper>
        </ScreenContainer>
    );
};

export default ListsFC;
